import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GamesActions } from '../actions/actions-type.action';
import { GamesService } from '../../services/games.service';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import { NotificationsService } from '@shared/services/notifications.service';
import { BadResponseWithError } from '@shared/models/common.interface';

@Injectable({ providedIn: 'root' })
export class GamesEffect {
    fetchPlatforms$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchPlatformsRequests),
            switchMap(() =>
                this.gameService.fetchPlatforms().pipe(
                    map((response) =>
                        GamesActions.fetchPlatformsSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: any) => {
                        this.notifications.showError(response.message);
                        return of(
                            GamesActions.fetchPlatformsFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.fetchGamesRequests),
            switchMap(() =>
                this.gameService.fetchGames().pipe(
                    map((response) =>
                        GamesActions.fetchGamesSuccess({ payload: response })
                    ),
                    catchError((response: any) => {
                        this.notifications.showError(response.message);
                        return of(
                            GamesActions.fetchGamesFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.createGameRequests),
            switchMap(({ payload }) =>
                this.gameService.createGame(payload).pipe(
                    mergeMap((res) => {
                        this.notifications.showSuccessMessage(
                            'Game created successfully'
                        );
                        const sideEffects: any[] = [
                            GamesActions.fetchPlatformsRequests(),
                        ];
                        return [
                            GamesActions.createGameSuccess({
                                payload: { ...payload, id: res },
                            }),
                            ...sideEffects,
                        ];
                    }),
                    catchError((response: any) => {
                        this.notifications.showError(response.message);
                        return of(
                            GamesActions.createGameFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.updateGameRequests),
            switchMap(({ body }) =>
                this.gameService.updateGame(body).pipe(
                    map(() => {
                        this.notifications.showSuccessMessage(
                            'Game updated successfully'
                        );
                        return GamesActions.updateGameSuccess({ body });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notifications.showError(response.error.message);
                        return of(
                            GamesActions.updateGameFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GamesActions.deleteGameRequests),
            switchMap(({ gameId }) =>
                this.gameService.deleteGame(gameId).pipe(
                    map(() => {
                        this.notifications.showSuccessMessage(
                            'Game deleted successfully'
                        );
                        return GamesActions.deleteGameSuccess({ gameId });
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notifications.showError(response.error.message);
                        return of(
                            GamesActions.deleteGameFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private gameService: GamesService,
        private notifications: NotificationsService
    ) {}
}
